import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const pricingTiers = [
  {
    name: 'Starter', price: 999,
    tagline: 'For individuals ready to reclaim their time.',
    highlighted: false,
    features: ['Cloud-hosted deployment (VPS — secure, dedicated instance)', 'Email management (Gmail or Outlook)', 'Calendar scheduling and optimisation', '3 integrations (email, calendar, + 1 messaging channel)', 'Meeting prep and follow-up drafting', '30-day post-setup support', 'Done-with-you onboarding'],
  },
  {
    name: 'Pro', price: 1999,
    tagline: 'For advisors, insurance professionals, and client-heavy operations.',
    highlighted: true,
    features: ['Everything in Starter, plus:', 'Unlimited integrations', 'CRM integration (HubSpot, Salesforce, GHL, or Pipedrive)', 'Advanced automation workflows', 'Custom knowledge base training', 'Priority response during setup', 'Done-with-you onboarding with extended support'],
  },
  {
    name: 'Premium', price: 2499,
    tagline: 'Maximum security. Zero cloud exposure. Shipped to your door.',
    highlighted: false,
    features: ['Everything in Pro, plus:', 'Pre-configured Mac mini hardware — shipped ready to plug in', 'Complete data sovereignty — your data never leaves your building', 'Air-gapped option for sensitive industries', 'White-label platform rights', 'On-site or remote deployment support'],
  },
];

const PricingTier = ({ tier, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    className={`relative rounded-2xl flex flex-col ${tier.highlighted ? 'lg:-mt-4 lg:mb-4' : ''}`}
  >
    {tier.highlighted && (
      <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2A5C8A] to-transparent" />
    )}
    <div className={`relative p-8 rounded-2xl flex flex-col flex-grow h-full ${
      tier.highlighted
        ? 'bg-[#1A1A18] text-[#F7F5F2] border border-[#2A2A26]'
        : 'bg-white border border-[#E4DFD8]'
    }`}>
      {tier.highlighted && (
        <div className="absolute top-4 right-4 px-2.5 py-1 bg-[#2A5C8A] rounded-full text-[9px] font-mono-custom font-semibold uppercase tracking-wider text-white">
          Most Popular
        </div>
      )}
      <div className="mb-7">
        <p className={`font-mono-custom text-[9px] tracking-[0.25em] uppercase font-semibold mb-3 ${tier.highlighted ? 'text-[#7AAAC8]' : 'text-[#2A5C8A]'}`}>
          {tier.name}
        </p>
        <div className="flex items-baseline gap-2 mb-2">
          <span className={`text-4xl font-bold tracking-tight ${tier.highlighted ? 'text-[#F7F5F2]' : 'text-[#1A1A18]'}`}>
            ${tier.price.toLocaleString()}
          </span>
          <span className={`text-sm ${tier.highlighted ? 'text-[#9E9690]' : 'text-[#9E9690]'}`}>one-time</span>
        </div>
        <p className={`text-sm leading-relaxed ${tier.highlighted ? 'text-[#9E9690]' : 'text-[#6B6560]'}`}>{tier.tagline}</p>
      </div>

      <ul className="space-y-3.5 mb-8 flex-grow">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${tier.highlighted ? 'bg-[#2A5C8A]' : 'bg-[#EBF2F9]'}`}>
              <SafeIcon icon={FiIcons.FiCheck} className={`text-[9px] ${tier.highlighted ? 'text-white' : 'text-[#2A5C8A]'}`} />
            </div>
            <span className={`text-sm leading-relaxed ${tier.highlighted ? 'text-[#C8C0B4]' : 'text-[#6B6560]'}`}>{f}</span>
          </li>
        ))}
      </ul>

      <button className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
        tier.highlighted
          ? 'bg-white text-[#1A1A18] hover:bg-[#F0EDE8]'
          : 'bg-[#1A1A18] text-[#F7F5F2] hover:bg-[#2A2A26]'
      }`}>
        Get Started
        <SafeIcon icon={FiIcons.FiArrowRight} className="text-xs" />
      </button>
    </div>
  </motion.div>
);

const Pricing = () => (
  <section id="pricing" className="py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-14">
        <div className="tag-pill mx-auto mb-5 w-fit">Transparent Pricing</div>
        <h2 className="text-3xl md:text-5xl font-extrabold text-[#1A1A18] mb-4 tracking-tight leading-tight" style={{ letterSpacing: '-0.02em' }}>
          A full-time EA costs{' '}
          <span className="gradient-text-accent">$5,000+ per month.</span>
        </h2>
        <p className="text-lg text-[#6B6560] max-w-xl mx-auto">
          Your AI assistant: one-time setup, yours forever.
        </p>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center text-[#6B6560] max-w-2xl mx-auto mb-12 text-sm leading-relaxed"
      >
        WeekOne is priced as infrastructure, not a subscription. You pay for setup once.
        The assistant runs on your infrastructure. No ongoing fees.
      </motion.p>

      <div className="grid lg:grid-cols-3 gap-5 mb-10">
        {pricingTiers.map((tier, i) => <PricingTier key={tier.name} tier={tier} index={i} />)}
      </div>

      {/* Add-on */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-2xl mx-auto mb-10"
      >
        <div className="bg-[#F7F5F2] border border-[#E4DFD8] rounded-2xl px-7 py-5 flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <p className="font-mono-custom text-[9px] tracking-[0.25em] text-[#3D7A5E] uppercase font-semibold mb-1">Optional Add-on</p>
            <h3 className="text-base font-bold text-[#1A1A18] mb-0.5">Ongoing Support</h3>
            <p className="text-[#6B6560] text-sm">Quarterly reviews, updates, and priority support</p>
          </div>
          <div className="flex-shrink-0">
            <span className="text-3xl font-bold text-[#1A1A18]">$249</span>
            <span className="text-[#9E9690] text-sm ml-1">/month</span>
          </div>
        </div>
      </motion.div>

      {/* OpenClaw note */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto mb-10 px-6 py-4 bg-[#F7F5F2] border border-[#E4DFD8] rounded-xl flex items-start gap-4"
      >
        <SafeIcon icon={FiIcons.FiServer} className="text-[#6B6560] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-[#1A1A18] mb-1">Built on OpenClaw</p>
          <p className="text-sm text-[#6B6560] leading-relaxed">
            Your assistant operates on open-source infrastructure you control. We configure it. You own it.
            No black boxes, no data harvesting, no monthly SaaS tax on productivity.
          </p>
        </div>
      </motion.div>

      {/* Guarantee */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#F7F5F2] border border-[#E4DFD8]">
          <SafeIcon icon={FiIcons.FiShield} className="text-[#2A5C8A] flex-shrink-0" />
          <span className="text-sm text-[#6B6560]">
            <strong className="text-[#1A1A18]">30-day satisfaction guarantee.</strong>{' '}
            If your assistant isn't saving you time by day 30, we'll troubleshoot, retrain, or refund in full.
          </span>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center">
        <button className="btn-primary text-[15px] px-10 py-4 flex items-center gap-2 mx-auto">
          Book your 15-minute intro
          <SafeIcon icon={FiIcons.FiArrowRight} />
        </button>
      </motion.div>
    </div>
  </section>
);

export default Pricing;